import { ApplicationInsights } from '@microsoft/applicationinsights-web';
import { generateW3CId } from '@microsoft/applicationinsights-core-js';
import { useMsal } from '@azure/msal-react';
import { createContext, type ReactNode, useEffect, useState } from 'react';
import { getEnvConfig } from '../utils/shared.environment.utils';
import { router } from '@/router';

export const ApplicationInsightsContext = createContext<ApplicationInsights | null>(null);

export interface ApplicationInsightsProviderProps {
  children: ReactNode;
}

export function ApplicationInsightsProvider({children}: ApplicationInsightsProviderProps) {
  const [appInsights, setAppInsights] = useState<ApplicationInsights | null>(null);
  const {accounts} = useMsal();

  useEffect(() => {
    const connectionString = getEnvConfig().VITE_APPLICATIONINSIGHTS_CONNECTION_STRING;

    if (!connectionString) {
      console.warn('Application Insights connection string not configured');
      return;
    }

    const appInsightsInstance = new ApplicationInsights({
      config: {
        connectionString,
        enableAutoRouteTracking: true,
        enableCorsCorrelation: true,
      },
    });

    appInsightsInstance.loadAppInsights();
    appInsightsInstance.trackPageView();

    setAppInsights(appInsightsInstance);

    // Track TanStack Router navigation events
    const unsubscribe = router.subscribe('onResolved', (event) => {
      // Only track when path actually changed (not just search params)
      if (event.pathChanged) {
        // Clear the current operation context to start a fresh trace for this page
        // Without this, all API calls would be nested under the initial page view's trace
        appInsightsInstance.context.telemetryTrace.traceID = generateW3CId();
        appInsightsInstance.context.telemetryTrace.parentID = undefined;

        appInsightsInstance.trackPageView({
          name: event.toLocation.pathname,
          uri: event.toLocation.href,
        });
      }
    });

    return () => {
      unsubscribe();
    };
  }, []);

  // Set authenticated user context when user logs in/out
  useEffect(() => {
    if (!appInsights) return;

    const account = accounts[0];
    if (account?.localAccountId) {
      // localAccountId is the Entra Object ID (oid claim)
      // This sets user_AuthenticatedId in Application Insights
      appInsights.setAuthenticatedUserContext(account.localAccountId, undefined, true);
    } else {
      appInsights.clearAuthenticatedUserContext();
    }
  }, [appInsights, accounts]);

  return (
    <ApplicationInsightsContext.Provider value={appInsights}>
      {children}
    </ApplicationInsightsContext.Provider>
  );
}
